import { AutopilotConnectionError } from '@llmor/autopilot-core';
import type { FunctionCall, FunctionCallRecord } from '@llmor/autopilot-core';
import { ClientError } from '@/api/client';

export interface AutopilotUiError {
  title: string;
  message: string;
  detail?: string;
}

const SURFACED_ERROR_CONTEXTS = new Set([
  'autopilot',
  'refresh_conversation',
  'conversation_event',
  'send_function_response',
  'socket_error',
]);

export function shouldSurfaceClientError(context: string): boolean {
  return SURFACED_ERROR_CONTEXTS.has(context);
}

function parseJsonObject(value: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function messageFromPayload(payload: Record<string, unknown>): string | undefined {
  for (const key of ['message', 'error', 'detail']) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

function stripLibPrefix(message: string): string {
  return message.replace(/^LLMOR AutopilotClient:\s*/i, '').trim();
}

function prettyDetail(raw?: string): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const parsed = parseJsonObject(trimmed);
  if (parsed) {
    const nested = messageFromPayload(parsed);
    if (nested && nested !== trimmed) return nested;
    try {
      return JSON.stringify(parsed, null, 2);
    } catch {
      return trimmed;
    }
  }
  return trimmed.length > 400 ? `${trimmed.slice(0, 400)}…` : trimmed;
}

/** Turns a thrown value into a short title + message the Autopilot panel can render. */
export function formatAutopilotError(error: unknown): AutopilotUiError {
  if (error instanceof AutopilotConnectionError) {
    const title =
      error.kind === 'http'
        ? `Server error${error.status ? ` (HTTP ${error.status})` : ''}`
        : error.kind === 'network'
          ? 'Cannot reach the server'
          : error.kind === 'offline'
            ? 'You appear to be offline'
            : 'Something went wrong';
    const detail = prettyDetail(error.hint);
    const rawMessage = stripLibPrefix(error.message || '');
    const message = detail && detail !== rawMessage ? rawMessage : rawMessage || 'The autopilot request failed.';
    return {
      title,
      message,
      detail: detail && detail !== message ? detail : undefined,
    };
  }

  if (error instanceof ClientError) {
    const status = error.response?.status;
    const apiMessage = typeof error.responseJson?.message === 'string' ? error.responseJson.message.trim() : '';
    return {
      title: `Request failed${status ? ` (HTTP ${status})` : ''}`,
      message: apiMessage || error.message || 'The request failed.',
    };
  }

  if (error instanceof Error && error.message.trim()) {
    return { title: 'Something went wrong', message: stripLibPrefix(error.message) };
  }

  return { title: 'Something went wrong', message: 'An unexpected error occurred.' };
}

function parsedFunctionResult(result?: string | null): Record<string, unknown> | null {
  if (!result) return null;
  return parseJsonObject(result);
}

/** True when the client recorded a handler throw, or the function returned `{ success: false }`. */
export function functionCallFailed(call: FunctionCall, history: FunctionCallRecord[]): boolean {
  const record = history.find((entry) => entry.id === call.id);
  if (record?.status === 'error') return true;
  const parsed = parsedFunctionResult(call.result);
  return parsed?.success === false;
}

export function functionCallErrorMessage(call: FunctionCall, history: FunctionCallRecord[]): string {
  const parsed = parsedFunctionResult(call.result);
  if (parsed) {
    const nested = messageFromPayload(parsed);
    if (nested) return nested;
  }
  if (typeof call.result === 'string' && call.result.trim()) {
    return call.result.trim();
  }
  const record = history.find((entry) => entry.id === call.id);
  if (record?.status === 'error') return 'This action failed.';
  return 'This action failed.';
}
